import { createFileRoute, Link } from '@tanstack/react-router';
import { submissionHistoryQuery } from '~/util/queries/public/submissions';
import { submissionQuery } from '~/util/queries/staff/submissions';
import { UserTags } from '~/partials/Tags';
import { Separator } from '~/primitives/separator';
import { useAppForm } from '~/util/hooks/useAppForm';
import {
    submissionFormInput,
    submissionFormSchema,
} from '~/util/forms/submission';
import { useList } from '~/util/hooks/useList';
import { formOptions } from '@tanstack/react-form';
import { useEffect, useState } from 'react';
import {
    ExternalLink,
    Info,
    LockKeyhole,
    Pencil,
    PencilRuler,
} from 'lucide-react';
import TooltipButton from '~/partials/Tooltip';
import { EditSubmissionSheet } from './-EditSubmissionSheet';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { editSubmissionMutation } from '~/util/mutations/public/submissions';
import { StaffSubmissionHistory } from './-SubmissionHistory';
import {
    cn,
    formatLevelGDID,
    getCompletionTime,
    getLevelDisplayName,
    getLevelDisplayPrefix,
    seo,
} from '~/util';
import { ToolsModal } from './-ToolsModal';
import { DiscordProfileLink } from '~/partials/DiscordProfileLink';
import { VideoPlayer } from '~/components/VideoPlayer';
import { userProfileQuery } from '~/util/queries/public/profiles';
import { Button } from '~/primitives/button';
import { deleteRecordStaffMutation } from '~/util/mutations/staff/records';
import { useAuthData, useScopes } from '~/components/Providers/AuthProvider';
import { DisableFormContextProvider } from '~/components/Forms/DisableContext';
import { levelCustomCopiesQuery } from '~/util/queries/public/levels';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/primitives/tooltip';
import { LevelCustomCopyPost } from '~/components/LevelCustomCopyPost';
import { StaffLevelNotes } from './-LevelNotes';
import { StaffLevelUpdates } from './-LevelUpdates';

export const Route = createFileRoute(
    '/_authenticated/staff/_submissionsLayout/submissions/$submissionid',
)({
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({
        context: { queryClient },
        params: { submissionid },
        deps: { list },
    }) => {
        const submission = await queryClient.fetchQuery(
            submissionQuery({
                list,
                id: submissionid,
            }),
        );

        const [history, submitterProfile, levelCustomCopies] =
            await Promise.all([
                queryClient.fetchQuery(
                    submissionHistoryQuery({
                        list,
                        submission_id: submissionid,
                    }),
                ),
                queryClient.fetchQuery(
                    userProfileQuery({
                        list,
                        userid: submission.submitted_by.id,
                    }),
                ),
                queryClient.fetchQuery(
                    levelCustomCopiesQuery({
                        list,
                        levelid: submission.level.id,
                    }),
                ),
            ]);
        return {
            submission,
            history,
            submitterProfile,
            levelCustomCopies,
        };
    },
    head: ({ loaderData }) => {
        const submission = loaderData?.submission;
        return {
            meta: seo({
                title: submission
                    ? `${getLevelDisplayName(submission.level)} - ${submission?.submitted_by.global_name} (${submission?.status})`
                    : 'Unknown Submission',
            }),
        };
    },
    component: Submission,
});

function Submission() {
    const list = useList();

    const { id } = useAuthData() ?? {};
    const { submission, submitterProfile, levelCustomCopies } =
        Route.useLoaderData({
            select: ({ submission, submitterProfile, levelCustomCopies }) => ({
                submission,
                submitterProfile,
                levelCustomCopies,
            }),
        });

    const acceptedRecords = submitterProfile.records;

    const customCopy = submission.custom_copy_id
        ? levelCustomCopies.find(
              (custom_copy) =>
                  custom_copy.copy_id === submission.custom_copy_id,
          )
        : undefined;

    const customCopyType = submission.custom_copy_id
        ? submission.custom_copy_id == submission.level.level_id
            ? 'Allowed'
            : (customCopy?.status ?? 'Unknown')
        : undefined;

    const correspondingRecord = acceptedRecords?.find(
        (record) => record.level.id === submission.level.id,
    );

    const [editMode, setEditMode] = useState(false);
    const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);

    const editMutation = useApiMutation(editSubmissionMutation());
    const deleteRecordMutation = useApiMutation(deleteRecordStaffMutation());

    const isOwnSubmission = id === submission.submitted_by.id;

    const { hasScope } = useScopes();

    const form = useAppForm(
        formOptions({
            defaultValues: {
                list,
                ...submission,
                reviewer_notes: null,
                private_reviewer_notes: null,
            } as submissionFormInput,
            validators: {
                onChange: submissionFormSchema,
                onChangeAsyncDebounceMs: 500,
            },
            onSubmit: async ({ value }) => {
                await editMutation.mutateAsync({
                    values: value,
                    id: submission.id,
                });
                if (
                    value.status === 'Denied' &&
                    correspondingRecord &&
                    value.delete_record_on_deny
                ) {
                    deleteRecordMutation.mutate({
                        list,
                        recordid: correspondingRecord.id,
                        userid: submission.submitted_by.id,
                    });
                }
            },
        }),
    );

    useEffect(() => {
        form.reset();
    }, [form, submission]);

    return (
        <>
            {correspondingRecord && (
                <div
                    className={cn(
                        'flex justify-between items-center gap-2 text-white mt-5 px-2 py-1 border-2 rounded-lg bg-pending/30 border-pending',
                    )}
                >
                    <p>
                        <Info className="inline size-6 mr-2" />
                        This submission was accepted previously and this user
                        already has a record for this level.
                    </p>
                    <Link
                        to="/staff/records/$recordid"
                        params={{
                            recordid: correspondingRecord.id,
                        }}
                        search={({ list }) => ({ list })}
                        target="_blank"
                    >
                        <Button className="w-full gap-2" variant="secondary">
                            Open record <ExternalLink />
                        </Button>
                    </Link>
                </div>
            )}
            {isOwnSubmission && (
                <div
                    className={cn(
                        'flex justify-between items-center gap-2 text-white mt-5 p-2 border-2 rounded-lg bg-destructive/30 border-destructive',
                    )}
                >
                    <p>
                        <Info className="inline size-6 mr-2" />
                        You cannot edit your own submission.
                    </p>
                </div>
            )}
            <div className="flex flex-col my-4 gap-4">
                <div className="flex flex-col xl:flex-row w-full items-center justify-center gap-4">
                    <div className="w-full xl:w-1/2 h-full rounded-xl">
                        <VideoPlayer videoUrl={submission.video_url} />
                    </div>
                    <div className="w-full xl:w-1/3 xl:border-l-2 xl:border-secondary-light p-2 xl:pl-6 xl:ml-6">
                        <form
                            id="edit-submission-staff"
                            onSubmit={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                form.handleSubmit();
                            }}
                        >
                            <DisableFormContextProvider value={isOwnSubmission}>
                                <h2 className="text-3xl font-semibold leading-tight">
                                    <span className="text-secondary-light">
                                        {getLevelDisplayPrefix(
                                            submission.level,
                                        )}
                                    </span>{' '}
                                    <Link
                                        to="/list/$levelid"
                                        search={({ list }) => ({ list })}
                                        params={{
                                            levelid: formatLevelGDID(
                                                submission.level,
                                            ),
                                        }}
                                        target="_blank"
                                    >
                                        {submission.level.name}
                                    </Link>
                                    {submission.locked && (
                                        <LockKeyhole className="inline size-10 text-destructive" />
                                    )}
                                </h2>
                                {submission.level.status === 'Pending' &&
                                submission.level.requires_raw_footage ? (
                                    <div className="flex items-center gap-2 text-pending">
                                        <Info className="size-8" />
                                        <p className="border-l border-pending pl-2 my-2">
                                            This pending level is expected to
                                            place in the top 400 and requires
                                            raw footage to be submitted.
                                        </p>
                                    </div>
                                ) : null}
                                <div className="flex flex-col">
                                    <h3 className="font-bold">Submitted by:</h3>
                                    <div className="flex items-center gap-2">
                                        <UserTags
                                            highContrast
                                            showFullName
                                            item={submission.submitted_by}
                                        />
                                        <DiscordProfileLink
                                            id={
                                                submission.submitted_by
                                                    .discord_id
                                            }
                                        />
                                        <p>
                                            {(acceptedRecords?.length ?? 0 > 0)
                                                ? `${acceptedRecords?.length} records`
                                                : 'No accepted records yet'}
                                        </p>
                                    </div>
                                </div>
                                <Separator className="my-2" />
                                <div className="flex flex-col xl:flex-row xl:justify-between">
                                    <div>
                                        <div className="flex gap-8">
                                            <div className="flex flex-col">
                                                <h3 className="font-bold">
                                                    Device:
                                                </h3>
                                                <p>
                                                    {submission.mobile
                                                        ? 'Mobile'
                                                        : 'PC'}
                                                </p>
                                            </div>
                                            <div className="flex flex-col">
                                                <h3 className="font-bold">
                                                    Mod menu:
                                                </h3>
                                                <p>
                                                    {submission.mod_menu ??
                                                        'N/A'}
                                                </p>
                                            </div>
                                            {submission.completion_time !==
                                                undefined && (
                                                <div className="flex flex-col">
                                                    <h3 className="font-bold">
                                                        Completion time:
                                                    </h3>
                                                    <p>
                                                        {getCompletionTime(
                                                            submission.completion_time,
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                            <div className="flex flex-col">
                                                <h3 className="font-bold">
                                                    Custom Copy ID:
                                                </h3>
                                                <div
                                                    className={
                                                        customCopyType ===
                                                            'Allowed' ||
                                                        customCopyType ===
                                                            'Published'
                                                            ? 'text-confirm'
                                                            : customCopyType ===
                                                                'Banned'
                                                              ? 'text-destructive'
                                                              : customCopyType ===
                                                                  'Unknown'
                                                                ? 'text-claimed'
                                                                : ''
                                                    }
                                                >
                                                    {submission.custom_copy_id ??
                                                        'N/A'}
                                                    {submission.custom_copy_id && (
                                                        <Tooltip>
                                                            <TooltipTrigger
                                                                asChild
                                                            >
                                                                <Info className="inline size-4 ml-1" />
                                                            </TooltipTrigger>
                                                            <TooltipContent className="bg-transparent border-none">
                                                                {submission.custom_copy_id ===
                                                                submission.level
                                                                    .level_id ? (
                                                                    <p className="p-2 bg-secondary-light rounded-xl">
                                                                        This is
                                                                        the
                                                                        level&apos;s
                                                                        own ID.
                                                                    </p>
                                                                ) : (
                                                                    <LevelCustomCopyPost
                                                                        custom_copy_data={
                                                                            customCopy
                                                                        }
                                                                        copy_id={
                                                                            submission.custom_copy_id
                                                                        }
                                                                        allowClick={
                                                                            false
                                                                        }
                                                                        showStatusWhenAllowed
                                                                    />
                                                                )}
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col">
                                            <h3 className="font-bold">
                                                Completion link:
                                            </h3>
                                            <a
                                                href={
                                                    submission.video_url ?? '#'
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-500 underline break-all hover:text-blue-700"
                                            >
                                                {submission.video_url ?? 'N/A'}
                                            </a>
                                        </div>
                                        <div className="flex flex-col">
                                            <h3 className="font-bold">
                                                Raw footage link:
                                            </h3>
                                            {submission.raw_url ? (
                                                <a
                                                    href={
                                                        submission.raw_url ??
                                                        '#'
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-500 underline break-all hover:text-blue-700"
                                                >
                                                    {submission.raw_url ??
                                                        'N/A'}
                                                </a>
                                            ) : (
                                                'N/A'
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 self-end xl:self-auto">
                                        <TooltipButton
                                            text="Edit full submission data"
                                            side="top"
                                        >
                                            <Pencil
                                                className="size-8 cursor-pointer hover:text-secondary-light"
                                                onClick={() =>
                                                    setEditMode(true)
                                                }
                                            />
                                        </TooltipButton>
                                        {hasScope(
                                            'submission_edit_with_raw_footage',
                                        ) && (
                                            <TooltipButton
                                                text="Open tools"
                                                side="top"
                                            >
                                                <PencilRuler
                                                    className="h-8 w-8 cursor-pointer hover:text-secondary-light"
                                                    onClick={() =>
                                                        setIsToolsModalOpen(
                                                            true,
                                                        )
                                                    }
                                                />
                                            </TooltipButton>
                                        )}
                                    </div>
                                </div>
                                <Separator className="my-2" />
                                <div className="flex flex-col xl:flex-row gap-4 mb-4">
                                    <form.AppField name="status">
                                        {(field) => (
                                            <field.SubmissionStatusField
                                                placeholder="Status"
                                                label="Status"
                                            />
                                        )}
                                    </form.AppField>
                                    <form.AppField name="reviewer_notes">
                                        {(field) => (
                                            <field.TextAreaField
                                                placeholder="Public reviewer notes"
                                                label="Reviewer notes"
                                                classname="bg-none hover:bg-none"
                                            />
                                        )}
                                    </form.AppField>
                                    <form.AppField name="private_reviewer_notes">
                                        {(field) => (
                                            <field.TextAreaField
                                                placeholder="Private notes (for other reviewers only)"
                                                label="Private notes"
                                                classname="bg-none hover:bg-none"
                                            />
                                        )}
                                    </form.AppField>
                                </div>
                                <form.Subscribe
                                    selector={({ values: { status } }) =>
                                        status
                                    }
                                >
                                    {(status) =>
                                        status == 'Denied' &&
                                        correspondingRecord && (
                                            <form.AppField name="delete_record_on_deny">
                                                {(field) => (
                                                    <field.CheckboxField
                                                        hideLabelAndErrors
                                                        switchLabel="Also delete the existing record when denying this submission"
                                                    />
                                                )}
                                            </form.AppField>
                                        )
                                    }
                                </form.Subscribe>
                                <form.AppForm>
                                    <form.SubscribeButton
                                        label="Save changes"
                                        classname="w-full"
                                    />
                                </form.AppForm>
                            </DisableFormContextProvider>
                        </form>
                    </div>
                </div>
                <StaffLevelNotes />
                <StaffLevelUpdates />
                <StaffSubmissionHistory />
            </div>
            <EditSubmissionSheet open={editMode} onOpenChange={setEditMode} />
            {hasScope('submission_edit_with_raw_footage') && (
                <ToolsModal
                    open={isToolsModalOpen}
                    onOpenChange={setIsToolsModalOpen}
                />
            )}
        </>
    );
}
